// Adding the grammar
import "prism-react-editor/prism/languages/jsx";

// Adds comment toggling and auto-indenting
import "prism-react-editor/languages/html";
import "prism-react-editor/languages/json";

import "prism-react-editor/layout.css";
import "prism-react-editor/themes/github-dark.css";

// Required by the basic setup
import "prism-react-editor/search.css";

import { useMutation, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { Editor } from "prism-react-editor";
import { BasicSetup } from "prism-react-editor/setups";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { HttpException } from "../../models/exceptions";
import { useAppSelector } from "../../store";
import { fetchMailTemplateConfig } from "../../utils/http/mail-template-configs";
import {
  copyFromTemplateCustomerToDraft,
  deleteMailTemplate,
  fetchMailTemplates,
  fetchMailTemplateSends,
  postMailTemplate,
  promoteMailTemplate,
} from "../../utils/http/mail-templates";
import { queryClient } from "../../utils/http/queryClient";
import { buildV3TranslationKeyPrefix, extractTranslationKeys, qualifyTranslationKey } from "../../utils/translations";
import ChartComponent from "./ChartComponent";
import "./Template.css";
import TemplateConfig from "./TemplateConfig";
import TemplatePreview from "./TemplatePreview";
import TranslationsPanel from "./TranslationsPanel";

function Template() {
  const navigate = useNavigate();
  const params = useParams();
  const { language, automation, event, action, target, version } = params;
  const pathWithoutVersion = { language, event, action, target };
  const pathKey = `${language}.${event}.${action}.${target}.${version}`;
  const mailTemplatePath = { event, action, target };

  const activeCompany = useAppSelector((state) => state.company.activeCompany);

  const [subjectContent, setSubjectContent] = useState<any | null>(null);
  const [bodyContent, setBodyContent] = useState<any | null>(null);
  const [currentAdminData, setCurrentAdminData] = useState<any | null>(null);
  const [sendData, setSendData] = useState<any>({});

  const [draftExists, setDraftExists] = useState(false);
  const [unsaved, setUnsaved] = useState(false);

  // The debounced draft save runs from a timeout, so it must not read editor text or the
  // timeout id out of a render closure -- by the time it fires, the closure it was created
  // in can be several keystrokes old, and it would then save stale text. Refs always hold
  // the latest values.
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Which path the editors currently hold content for. Until this matches the path on
  // screen, no editor callback may be saved.
  const initializedPathRef = useRef<string | null>(null);
  const subjectGeneratedRef = useRef<string>("");
  const bodyGeneratedRef = useRef<string>("");
  // What still needs to reach the server, with the path it belongs to. Kept (not cleared)
  // when a save fails, so a failure leaves the edit pending instead of silently dropping it.
  const pendingSaveRef = useRef<{ path: any; subject: string; body: string } | null>(null);

  const [errors, setErrors] = useState<string[]>([]);

  const [showDeleteLiveModal, setShowDeleteLiveModal] = useState(false);
  const [deleteLiveConfirmation, setDeleteLiveConfirmation] = useState("");

  const { setShowSendTestModal } = useOutletContext<{ setShowSendTestModal: any }>();

  // Get our mailTemplates (they should be already fetched by the loader, hence the staleTime)
  const { data: templates } = useQuery({
    queryKey: ["mailTemplates", pathWithoutVersion],
    queryFn: ({ signal }) => fetchMailTemplates({ signal, mailTemplatePath: pathWithoutVersion }),
    staleTime: 2000,
  });

  // Get our mailTemplate Configs (they should be already fetched by the loader, hence the staleTime)
  const { data: templateConfig } = useQuery({
    queryKey: ["mailTemplates", "configs", mailTemplatePath],
    queryFn: ({ signal }) => fetchMailTemplateConfig({ signal, mailTemplatePath }),
    staleTime: 2000,
  });

  // Get our mailTemplate sends (they should be already fetched by the loader, hence the staleTime)
  const { data: templateSends } = useQuery({
    queryKey: ["mailTemplates", "sends"],
    queryFn: ({ signal }) => fetchMailTemplateSends({ signal }),
    staleTime: 2000,
  });

  useEffect(() => {
    let key = `${event}__${action}__${target}`;
    if (event === "INVALID_DATA") {
      key = "INVALID_DATA";
    }
    if (templateSends && key in templateSends) {
      setSendData(templateSends[key]);
    } else {
      setSendData({});
    }
  }, [params, templateSends]);

  useEffect(() => {
    setDraftExists(
      templates &&
        !!templates.find(
          (mt: any) =>
            mt.language === language &&
            mt.event === event &&
            mt.action === action &&
            mt.target === target &&
            mt.version === "draft",
        ),
    );
  }, [params, templates]);

  // Do some page initialization
  useEffect(() => {
    setErrors([]);

    // Wait for the templates to actually be there. Seeding the editors from `undefined`
    // gives them empty content, and an empty editor that then reports itself would save
    // that emptiness over the stored draft.
    if (!templates) return;
    // Seed once per path: re-seeding on every `templates` change would clobber whatever
    // the user is typing (and jump the cursor to the first line).
    if (initializedPathRef.current === pathKey) return;

    const template =
      templates &&
      templates.find(
        (mt: any) =>
          mt.language === language &&
          mt.event === event &&
          mt.action === action &&
          mt.target === target &&
          mt.version === version,
      );

    // Only overwrite the content if it is a new path (otherwise the cursor will jump to the first line)

    const subject = template ? template.subject : "";
    setSubjectContent(subject || "");
    subjectGeneratedRef.current = subject || "";

    const html = template ? template.html : "";
    setBodyContent(html || "");
    bodyGeneratedRef.current = html || "";

    // From here on, editor callbacks for this path are the user's own edits and may be
    // saved. Anything arriving before this point belongs to the previous path, or to an
    // editor that has not been filled in yet.
    initializedPathRef.current = pathKey;

    if (template?.promoted_by || template?.promoted_on) {
      const currentDataAdmin = {
        promotedBy: template?.promoted_by,
        promotedOn: "",
      };
      if (template?.promoted_on) {
        const promotedOn = DateTime.fromISO(template.promoted_on, { zone: "utc" }).setZone("Europe/Paris");
        currentDataAdmin["promotedOn"] = promotedOn.toFormat("dd/MM/yyyy HH:mm:ss ZZZZ");
      }
      setCurrentAdminData(currentDataAdmin);
    } else {
      setCurrentAdminData(null);
    }
  }, [pathKey, templates]);

  // Writes whatever is still pending. Awaits the request: `unsaved` may only be cleared
  // once the server has actually accepted the draft.
  const flushDraftSave = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    const pending = pendingSaveRef.current;
    if (!pending) return;
    pendingSaveRef.current = null;

    try {
      await saveToDraftAsync({
        mailTemplatePath: pending.path,
        subject: pending.subject,
        body: pending.body,
      });
      setUnsaved(false);
    } catch (e: any) {
      // Put the edit back so a later keystroke (or leaving the page) retries it, and say
      // so -- previously this failure was swallowed and the editor still showed "saved".
      pendingSaveRef.current = pending;
      setErrors([`Could not save the draft: ${e?.info ?? e?.message ?? e}`]);
    }
  };

  // Update the draft
  const { mutate: mutateSaveToDraft, mutateAsync: saveToDraftAsync } = useMutation({
    mutationFn: postMailTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", pathWithoutVersion] });
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "summary"] });
      // The preview is rendered server-side from the stored template, so it has to be
      // re-fetched whenever the stored template changes.
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "render"] });
      if (version !== "draft") {
        navigate(`/mail-templates/${language}/${automation}/${event}/${action}/${target}/draft`);
      }
    },
  });

  const copyToDraft = async () => {
    setErrors([]);
    await mutateSaveToDraft({
      mailTemplatePath: { language, event, action, target, version: "draft" },
      subject: subjectContent,
      body: bodyContent,
    });
  };

  // Copy from Template
  const { mutate: mutateCopyFromTemplate } = useMutation({
    mutationFn: copyFromTemplateCustomerToDraft,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", pathWithoutVersion] });
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "summary"] });
      // The preview is rendered server-side from the stored template, so it has to be
      // re-fetched whenever the stored template changes.
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "render"] });
      if (version !== "draft") {
        navigate(`/mail-templates/${language}/${automation}/${event}/${action}/${target}/draft`);
      }
    },
    onError: (e: HttpException) => {
      setErrors([...errors, e.info!]);
    },
  });

  const copyFromTemplateToDraft = async () => {
    setErrors([]);
    await mutateCopyFromTemplate({
      mailTemplatePath: { language, event, action, target },
    });
  };

  // Delete the draft
  const { mutate: mutateDeleteDraft } = useMutation({
    mutationFn: deleteMailTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", pathWithoutVersion] });
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "summary"] });
      // The preview is rendered server-side from the stored template, so it has to be
      // re-fetched whenever the stored template changes.
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "render"] });
      navigate(`/mail-templates/${language}/${automation}/${event}/${action}/${target}/live`);
    },
  });

  const deleteDraft = async () => {
    setErrors([]);
    await mutateDeleteDraft({ mailTemplatePath: { language, event, action, target, version: "draft" } });
  };

  // Delete the live version (only possible if the config is not active)
  const { mutate: mutateDeleteLive } = useMutation({
    mutationFn: deleteMailTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", pathWithoutVersion] });
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "summary"] });
      // The preview is rendered server-side from the stored template, so it has to be
      // re-fetched whenever the stored template changes.
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "render"] });
      setShowDeleteLiveModal(false);
      setDeleteLiveConfirmation("");
      navigate(`/mail-templates/${language}/${automation}/${event}/${action}`);
    },
    onError: (e: HttpException) => {
      setShowDeleteLiveModal(false);
      setDeleteLiveConfirmation("");
      setErrors([e.info || e.message]);
    },
  });

  const deleteLive = async () => {
    setErrors([]);
    await mutateDeleteLive({ mailTemplatePath: { language, event, action, target, version: "live" } });
  };

  // Promote the draft
  const { mutate: mutatePromoteDraft } = useMutation({
    mutationFn: promoteMailTemplate,
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", pathWithoutVersion] });
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "summary"] });
      // The preview is rendered server-side from the stored template, so it has to be
      // re-fetched whenever the stored template changes.
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "render"] });
      navigate(`/mail-templates/${language}/${automation}/${event}/${action}/${target}/live`);
    },
  });

  const promoteDraft = async () => {
    setErrors([]);
    // A promote within the debounce window would otherwise promote the draft as it was
    // before the last keystrokes.
    await flushDraftSave();
    await mutatePromoteDraft({ mailTemplatePath: { language, event, action, target, version: "draft" } });
  };

  const onUpdateText = ({ subject = null, body = null }: { subject?: string | null; body?: string | null }) => {
    // Only the draft version is ever saved. Editors on the live version are readOnly, but
    // the underlying editor can still fire onUpdate as a side effect of mounting/formatting
    // the initial content -- without this guard that would create a draft just from viewing
    // the live version.
    if (version !== "draft") return;

    // An editor that is not (yet) holding this path's content is not the user editing --
    // it is the editor being filled in, or still showing the previous template. Saving
    // that would write the wrong content, or empty content, over the stored draft.
    if (initializedPathRef.current !== pathKey) return;

    setErrors([]);

    const nextSubject = subject != null ? subject : subjectGeneratedRef.current;
    const nextBody = body != null ? body : bodyGeneratedRef.current;

    // Compare against the latest editor text, not against the content this path was loaded
    // with, and compare with != rather than truthiness -- emptying the subject or the body
    // is an edit that has to be saved like any other.
    if (nextSubject === subjectGeneratedRef.current && nextBody === bodyGeneratedRef.current) {
      return;
    }

    // Belt and braces against wiping a draft: an update that empties *both* editors at
    // once is not something anyone does by typing, so treat it as the editor reporting an
    // uninitialized state rather than as an edit.
    if (!nextSubject && !nextBody && (subjectGeneratedRef.current || bodyGeneratedRef.current)) {
      return;
    }

    if (subject != null) subjectGeneratedRef.current = subject;
    if (body != null) bodyGeneratedRef.current = body;
    setUnsaved(true);

    // The path is captured here, so a save that fires after navigating away still lands on
    // the template that was actually edited.
    pendingSaveRef.current = {
      path: { language, event, action, target, version: "draft" },
      subject: nextSubject,
      body: nextBody,
    };

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(flushDraftSave, 1000);
  };

  // Leaving the page (or switching template) within the debounce window used to drop the
  // last edit on the floor. Write it out instead.
  useEffect(() => {
    return () => {
      flushDraftSave();
    };
  }, [params]);

  // Translation keys used in the current subject/body
  const translationKeyPrefix = useMemo(
    () => buildV3TranslationKeyPrefix(event, action, target),
    [event, action, target],
  );
  const translationKeys = useMemo(
    () =>
      extractTranslationKeys([subjectGeneratedRef.current, bodyGeneratedRef.current]).map((key) =>
        qualifyTranslationKey(key, translationKeyPrefix),
      ),
    [subjectGeneratedRef.current, bodyGeneratedRef.current, translationKeyPrefix, unsaved],
  );

  // Shared between TranslationsPanel and TemplatePreview.
  const [translationStatus, setTranslationStatus] = useState<"draft" | "approved">("draft");

  return (
    <>
      <ChartComponent data={sendData} />

      <Container>
        {event !== "replacements" && <TemplateConfig />}
        <Row>
          <Col xs={5}>
            <h2 className="d-flex align-items-center gap-2">
              <span>Version: {version!.toUpperCase()}</span>
              {unsaved && (
                <span className="unsaved-marker">
                  <span className="unsaved-marker__dot" />
                  Unsaved
                </span>
              )}
            </h2>
          </Col>
          <Col className="text-end">
            {version === "live" && draftExists && (
              <Button
                className="p-2 m-1 btn-sm"
                onClick={() => navigate(`/mail-templates/${language}/${automation}/${event}/${action}/${target}/draft`)}
              >
                Go to Draft
              </Button>
            )}
            {version === "live" && !draftExists && (
              <>
                {activeCompany?.name !== "template" && (
                  <Button className="p-2 m-1 btn-sm" onClick={copyFromTemplateToDraft} disabled={false}>
                    Copy from Template
                  </Button>
                )}
                <Button className="p-2 m-1 btn-sm" onClick={copyToDraft} disabled={false}>
                  Copy to Draft
                </Button>
              </>
            )}
            {version === "live" && !templateConfig?.active && (
              <Button
                variant="danger"
                className="p-2 m-1 btn-sm"
                onClick={() => {
                  setDeleteLiveConfirmation("");
                  setShowDeleteLiveModal(true);
                }}
              >
                Delete live
              </Button>
            )}
            {version === "draft" && (
              <>
                <Button
                  className={`p-2 m-1 btn-sm ${unsaved ? "btn-secondary" : ""}`}
                  onClick={deleteDraft}
                  disabled={unsaved}
                >
                  Delete draft
                </Button>
                <Button
                  className={`p-2 m-1 btn-sm ${unsaved ? "btn-secondary" : ""}`}
                  onClick={promoteDraft}
                  disabled={unsaved}
                >
                  Promote draft
                </Button>
              </>
            )}
            {version !== "live" && (
              <Button
                className={`p-2 m-1 btn-sm ${unsaved ? "btn-secondary" : ""}`}
                onClick={() => navigate(`/mail-templates/${language}/${automation}/${event}/${action}/${target}/live`)}
                disabled={unsaved}
              >
                Show live version
              </Button>
            )}
            {(event !== "replacements" || target?.startsWith("TEMPLATE_")) && (
              <Button
                className={`p-2 m-1 btn-sm ${unsaved ? "btn-secondary" : ""}`}
                onClick={() => {
                  setErrors([]);
                  setShowSendTestModal("SINGLE");
                }}
                disabled={unsaved}
              >
                Send Test Mail
              </Button>
            )}
          </Col>
        </Row>
      </Container>

      <Modal show={showDeleteLiveModal} onHide={() => setShowDeleteLiveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete live version</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You are about to delete the <b>live</b> version of{" "}
            {`${event?.toUpperCase()} > ${action?.toUpperCase()} > ${target?.toUpperCase()}`} (the mail is currently not
            active). This cannot be undone.
          </p>
          <p>
            Type <b>live</b> to confirm:
          </p>
          <Form.Control
            autoFocus
            value={deleteLiveConfirmation}
            onChange={(e) => setDeleteLiveConfirmation(e.target.value)}
            placeholder="live"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteLiveModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteLive} disabled={deleteLiveConfirmation !== "live"}>
            Delete live
          </Button>
        </Modal.Footer>
      </Modal>

      {errors && errors.length != 0 && (
        <Alert className="alert alert-danger text-danger mt-3">
          {errors?.map((e) => {
            return <span key={e}>{e}</span>;
          })}
        </Alert>
      )}

      {/* Temporarily show this message. Let's remove it in a few weeks/months (once all variables are streamlines a bit more). */}
      {action === "reminder_sms" && (
        <Alert className="alert alert-danger text-danger mt-3">
          <b>Update 26/05/2024</b>:<br />
          <br />
          SMS content is now also generated in Watchtower. Please check following before activating SMS flows:
          <br />
          <ul>
            <li>
              Variables now follow the same convention as mails. e.g. %(name)s is no longer used, replace it with {"{{"}{" "}
              NAME {"}}"}
            </li>
            <li>
              Only following variables can be used:
              <br />
              LANGUAGE, PHONE (in case of testing, this will be one or more phones where the message would have been
              sent to), PHONE_USED (actual phone getting the message), NAME, DATE, DATE_RELATIVE, TSTMP (time or list of
              times in text format on when the visit(s) are), AMOUNT_OF_TASKS (in the case of owners, an indication
              about how many tasks the SMS talks), ADDRESS, BROKER (= repr. full name), RA_MOBILE
            </li>
            <li>It's SMS, no need to add {"<BR>"} tags. A newline overhere will translate in a newline in the SMS.</li>
            <li>Keep it short, it's SMS (so the longer it becomes, the more it will cost)</li>
          </ul>
          You can also test SMS separately from the rest of the flows by enabling the "Being Tested" flag.
        </Alert>
      )}
      <Container>
        {event !== "replacements" && action !== "reminder_sms" && (
          <>
            <h3 className="mt-3">Subject:</h3>
            <Editor
              language="jsx"
              value={subjectContent}
              onUpdate={(text) => onUpdateText({ subject: text })}
              readOnly={version === "live"}
              wordWrap={true}
            >
              {(editor) => <BasicSetup editor={editor} />}
            </Editor>
          </>
        )}

        {!templateConfig?.same_body_as && (
          <>
            <h3 className="mt-3">Body:</h3>
            <Editor
              language="jsx"
              value={bodyContent}
              onUpdate={(text) => onUpdateText({ body: text })}
              readOnly={version === "live"}
              wordWrap={true}
            >
              {(editor) => <BasicSetup editor={editor} />}
            </Editor>

            {currentAdminData && (
              <>
                <h3 className="mt-3">Admin:</h3>
                <Container>
                  <Row>
                    <Col sm={3}>Promoted by:</Col>
                    <Col>{currentAdminData.promotedBy}</Col>
                  </Row>
                  <Row>
                    <Col sm={3}>Promoted on:</Col>
                    <Col>{currentAdminData.promotedOn}</Col>
                  </Row>
                </Container>
              </>
            )}
          </>
        )}

        {translationKeys.length > 0 && (
          <>
            <h3 className="mt-3">Translations:</h3>
            <p className="text-muted small">
              Every <code>t("key")</code> used in the subject/body above -- edit here instead of storing a full
              separate copy per language.
            </p>
            <TranslationsPanel
              keys={translationKeys}
              status={translationStatus}
              onStatusChange={setTranslationStatus}
            />
          </>
        )}

        {!templateConfig?.same_body_as && (
          <>
            {/* The heading lives in TemplatePreview: it shares a row with the language
                toggle. */}
            <TemplatePreview
              mailTemplatePath={{ language, automation, event, action, target, version }}
              translationStatus={translationStatus}
            />
          </>
        )}
      </Container>
    </>
  );
}

export default Template;
